import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { publicGet, publicPost } from '@/lib/public-api';
import { dateTime, humanize } from '@/lib/utils';
import { Badge, Button, SkeletonText } from '@/components/ui/primitives';

interface ProposedChange {
  from: string | null;
  to: string | null;
}

interface IdentityChange {
  id: string;
  uhid: string;
  masterName: string;
  facilityName: string | null;
  changes: Record<string, ProposedChange>;
  proposedAt: string;
}

const FIELD_LABELS: Record<string, string> = {
  fullName: 'Name',
  sex: 'Sex',
  dob: 'Date of birth',
  cnic: 'CNIC',
  phone: 'Mobile',
  altPhone: 'Other mobile',
  email: 'Email',
};

export function IdentityChangeAlerts() {
  const queryClient = useQueryClient();

  const changes = useQuery({
    queryKey: ['public-identity-changes'],
    queryFn: () => publicGet<IdentityChange[]>('/me/identity-changes'),
  });

  const decide = useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) =>
      publicPost(`/me/identity-changes/${id}`, { accept }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['public-identity-changes'] }),
  });

  if (changes.isLoading) return <SkeletonText lines={3} />;
  if (!changes.data || changes.data.length === 0) return null;

  return (
    <div className="mb-5 space-y-2">
      {changes.data.map((change) => (
        <div key={change.id} className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="inline-flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4" />
                {change.facilityName ?? 'A facility'} changed your details
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {change.masterName} · {change.uhid} · {dateTime(change.proposedAt)}
              </p>
            </div>
            <Badge variant="warning">Needs your answer</Badge>
          </div>

          <ul className="mt-2 space-y-0.5 text-sm">
            {Object.entries(change.changes).map(([field, value]) => (
              <li key={field}>
                <span className="text-muted-foreground">{FIELD_LABELS[field] ?? humanize(field)}: </span>
                <span className="line-through opacity-60">{value.from ?? '—'}</span>{' '}
                <span className="font-medium">› {value.to ?? '—'}</span>
              </li>
            ))}
          </ul>

          <p className="mt-2 text-xs text-muted-foreground">
            Accept and this becomes your record everywhere. Refuse and only that facility keeps it.
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              loading={decide.isPending && decide.variables?.id === change.id && decide.variables.accept}
              onClick={() => decide.mutate({ id: change.id, accept: true })}
            >
              That is correct
            </Button>
            <Button
              size="sm"
              variant="outline"
              loading={decide.isPending && decide.variables?.id === change.id && !decide.variables.accept}
              onClick={() => decide.mutate({ id: change.id, accept: false })}
            >
              No, leave mine
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
